import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Inbox, Paperclip, Search } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import Pagination from '../Pagination';
import { CATEGORIE_ARCHIVIO, annoElenco, etichettaCategoria, numeroBreve, type CategoriaArchivio } from '../../utils/circolariEtichette';
import { CircolareListItem, CircolariApi, formatNumeroCircolare } from '../../services/circolariService';

/**
 * Archivio circolari: filtri (anno, categoria, ricerca), elenco raggruppato per
 * anno e paginazione. Condiviso tra area riservata (associati) e area
 * associativa (rappresentanti): cambiano solo `api` e il link del dettaglio.
 * `onErroreAccesso` riceve gli errori delle chiamate e restituisce true se li
 * ha gestiti (non in regola, modulo spento, senza permesso): niente toast.
 */
export interface ElencoCircolariProps {
  api: CircolariApi;
  linkDettaglio: (id: string) => string;
  onErroreAccesso?: (error: any) => boolean;
}

const ITEMS_PER_PAGE = 20;
const SEARCH_DELAY_MS = 400;

const CARD = 'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800';
const SELECT =
  'w-full appearance-none pl-3 pr-9 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent';

export const formatDataCircolare = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
};

const ElencoCircolari: React.FC<ElencoCircolariProps> = ({ api, linkDettaglio, onErroreAccesso }) => {
  const navigate = useNavigate();
  const toast = useToast();

  const [items, setItems] = useState<CircolareListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [anni, setAnni] = useState<number[]>([]);
  const [anno, setAnno] = useState('');
  const [categoria, setCategoria] = useState<CategoriaArchivio | ''>('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Solo la risposta dell'ultima richiesta aggiorna l'elenco.
  const requestId = useRef(0);
  const gestisciAccesso = (error: any): boolean => !!onErroreAccesso && onErroreAccesso(error);

  // Anni disponibili per il filtro.
  useEffect(() => {
    let cancelled = false;
    api.getAnniCircolari()
      .then((list) => { if (!cancelled) setAnni(list); })
      .catch((error) => {
        if (cancelled) return;
        gestisciAccesso(error);
        // Altri errori: il filtro anno resta con «Tutti gli anni».
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  // Ricerca con ritardo: non si interroga il server a ogni tasto. Cambiare
  // filtro o ricerca riporta alla prima pagina, nello stesso aggiornamento
  // (una sola richiesta). Al montaggio i valori non cambiano: nessun effetto.
  const isFirstSearch = useRef(true);
  useEffect(() => {
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
      return;
    }
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(t);
  }, [search]);

  const changeAnno = (value: string) => { setAnno(value); setPage(1); };
  const changeCategoria = (value: string) => { setCategoria(value as CategoriaArchivio | ''); setPage(1); };

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    api.getCircolari({
      page,
      limit: ITEMS_PER_PAGE,
      anno: anno || undefined,
      categoria: categoria || undefined,
      search: debouncedSearch || undefined,
    })
      .then((res) => {
        if (id !== requestId.current) return;
        setItems(res.items);
        setTotal(res.total);
        setTotalPages(Math.max(1, res.totalPages));
      })
      .catch((error) => {
        if (id !== requestId.current) return;
        if (!gestisciAccesso(error)) toast.error('Errore nel caricamento delle circolari');
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, page, anno, categoria, debouncedSearch]);

  const filtriAttivi = !!(anno || categoria || debouncedSearch);

  return (
    <div>
      {/* Filtri */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <div className="relative">
          <select
            value={anno}
            onChange={(e) => changeAnno(e.target.value)}
            aria-label="Anno"
            className={SELECT}
          >
            <option value="">Tutti gli anni</option>
            {anni.map((a) => (
              <option key={a} value={String(a)}>{a}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
        <div className="relative">
          <select
            value={categoria}
            onChange={(e) => changeCategoria(e.target.value)}
            aria-label="Categoria"
            className={SELECT}
          >
            <option value="">Tutte le categorie</option>
            {CATEGORIE_ARCHIVIO.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
        <div className="relative sm:col-span-2">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            aria-hidden="true"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per numero (es. 479/SN/2026) o parola dell'oggetto..."
            aria-label="Cerca nelle circolari"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Lista */}
      <div className="mt-4">
        {loading ? (
          <div className="flex items-center justify-center py-16" aria-label="Caricamento">
            <div className="w-8 h-8 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className={`${CARD} text-center py-16 px-4`}>
            <Inbox className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
              Nessuna circolare trovata
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {filtriAttivi
                ? 'Prova a cambiare anno, categoria o testo di ricerca'
                : 'Quando UNASCA pubblicherà una circolare la troverai qui'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((c, i) => {
              // intestazioni dall'anno della data (come ordine e filtro del server), non dal protocollo
              const annoRiga = annoElenco(c);
              const nuovoAnno = i === 0 || annoElenco(items[i - 1]) !== annoRiga;
              const nAllegati = c.allegati?.length || 0;
              // senza numero UNASCA l'etichetta è il protocollo dell'ente (o «senza numero»), in grigio
              const senzaNumero = !numeroBreve(c);
              return (
                <React.Fragment key={c._id}>
                  {nuovoAnno && (
                    <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 pt-2 first:pt-0">
                      {annoRiga}
                    </h2>
                  )}
                  <button
                    type="button"
                    onClick={() => navigate(linkDettaglio(c._id))}
                    className={`w-full text-left ${CARD} p-4 transition-all hover:shadow-md`}
                  >
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                      <span className={senzaNumero ? 'font-medium text-gray-500 dark:text-gray-400' : 'font-semibold text-indigo-600 dark:text-indigo-400'}>
                        {formatNumeroCircolare(c)}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                        {etichettaCategoria(c.categoria)}
                      </span>
                      {c.dataCircolare && <span>{formatDataCircolare(c.dataCircolare)}</span>}
                      {nAllegati > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Paperclip className="w-3.5 h-3.5" aria-hidden="true" />
                          {nAllegati === 1 ? '1 allegato' : `${nAllegati} allegati`}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{c.oggetto}</p>
                  </button>
                </React.Fragment>
              );
            })}

            {totalPages > 1 && (
              <div className="mt-4">
                <Pagination
                  currentPage={Math.min(page, totalPages)}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  totalItems={total}
                  itemsPerPage={ITEMS_PER_PAGE}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ElencoCircolari;
